import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { supabase } from '@/lib/supabase';
import type { Database } from '@/types/database.generated';

export type JournalEntryRow = Database['public']['Tables']['journal_entries']['Row'];
export type JournalEntryWithAuthor = JournalEntryRow & {
  author_name: string | null;
};
export type JournalLockRow = Database['public']['Tables']['journal_locks']['Row'];
export type JournalAuditRow = Database['public']['Tables']['journal_audit']['Row'];
export type JournalEntryType = Database['public']['Enums']['journal_entry_type'];

const JOURNAL_ENTRY_SELECT =
  '*, author:users!journal_entries_created_by_fkey(name,email)';

type JournalEntryJoined = JournalEntryRow & {
  author: { name: string | null; email: string } | null;
};

function mapJournalEntry(row: JournalEntryJoined): JournalEntryWithAuthor {
  const { author, ...entry } = row;
  // Prefer frozen snapshot so name changes do not rewrite historical notes.
  return {
    ...entry,
    author_name:
      entry.author_name_snapshot ?? author?.name ?? author?.email ?? null,
  };
}

export interface JournalLockState {
  acquired: boolean;
  holder_user_id: string;
  holder_user_name: string;
  expires_at: string;
}

// ---- Query keys ----

export const journalEntriesKey = (clinicId: string | null | undefined, patientId?: string) =>
  ['journal', 'entries', clinicId ?? null, patientId ?? null] as const;

export const journalEntryKey = (id: string | null | undefined) =>
  ['journal', 'entry', id ?? null] as const;

export const journalLocksKey = (clinicId: string | null | undefined) =>
  ['journal', 'locks', clinicId ?? null] as const;

// ---- Queries ----

export function useJournalEntries(
  clinicId: string | null | undefined,
  opts: {
    patientId?: string;
    clinicianId?: string;
    from?: string;
    to?: string;
    includeArchived?: boolean;
    limit?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...journalEntriesKey(clinicId, opts.patientId), opts] as const,
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalEntryWithAuthor[]> => {
      if (!clinicId) return [];
      let q = supabase
        .from('journal_entries')
        .select(JOURNAL_ENTRY_SELECT)
        .eq('clinic_id', clinicId)
        .order('entry_date', { ascending: false })
        .order('created_at', { ascending: false })
        .limit(opts.limit ?? 200);

      if (opts.patientId) q = q.eq('patient_id', opts.patientId);
      if (opts.clinicianId) q = q.eq('clinician_id', opts.clinicianId);
      if (opts.from) q = q.gte('entry_date', opts.from);
      if (opts.to) q = q.lte('entry_date', opts.to);
      if (!opts.includeArchived) q = q.is('archived_at', null);

      const { data, error } = await q;
      if (error) throw error;
      return ((data ?? []) as unknown as JournalEntryJoined[]).map(mapJournalEntry);
    },
    staleTime: 10_000,
  });
}

export function useJournalEntry(id: string | null | undefined) {
  return useQuery({
    queryKey: journalEntryKey(id),
    enabled: Boolean(id),
    queryFn: async (): Promise<JournalEntryWithAuthor | null> => {
      if (!id) return null;
      const { data, error } = await supabase
        .from('journal_entries')
        .select(JOURNAL_ENTRY_SELECT)
        .eq('id', id)
        .single();
      if (error) throw error;
      return mapJournalEntry(data as unknown as JournalEntryJoined);
    },
    staleTime: 10_000,
  });
}

// ---- Live lock cache via Realtime ----

export function useJournalLocksRealtime(clinicId: string | null | undefined) {
  const qc = useQueryClient();

  const locksQuery = useQuery({
    queryKey: journalLocksKey(clinicId),
    enabled: Boolean(clinicId),
    queryFn: async (): Promise<JournalLockRow[]> => {
      if (!clinicId) return [];
      const { data, error } = await supabase
        .from('journal_locks')
        .select('*')
        .eq('clinic_id', clinicId);
      if (error) throw error;
      return data ?? [];
    },
    staleTime: 5_000,
  });

  useEffect(() => {
    if (!clinicId) return;

    const channel = supabase
      .channel(`journal_locks:clinic_id=eq.${clinicId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'journal_locks',
          filter: `clinic_id=eq.${clinicId}`,
        },
        () => {
          qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
        },
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [clinicId, qc]);

  return locksQuery;
}

// ---- Mutations ----

export function useCreateJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      patient_id: string;
      entry_type?: JournalEntryType;
      title?: string;
      content_md?: string;
      content_structured?: Record<string, any>;
      entry_date?: string;
      appointment_id?: string;
      template_id?: string;
    }) => {
      const { data, error } = await supabase.rpc('create_journal_entry', {
        p_clinic_id: clinicId!,
        p_patient_id: input.patient_id,
        p_entry_type: input.entry_type ?? 'generic',
        p_title: input.title ?? undefined,
        p_content_md: input.content_md ?? '',
        p_content_structured: input.content_structured ?? {},
        p_entry_date: input.entry_date ?? new Date().toISOString().slice(0, 10),
        p_appointment_id: input.appointment_id ?? undefined,
        p_template_id: input.template_id ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (entry && clinicId) {
        qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId) });
        qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId, entry.patient_id) });
      }
    },
  });
}

export function useUpdateJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      id: string;
      title?: string;
      content_md?: string;
      entry_date?: string;
    }) => {
      const { data, error } = await supabase.rpc('update_journal_entry', {
        p_id: input.id,
        p_title: input.title ?? undefined,
        p_content_md: input.content_md ?? undefined,
        p_entry_date: input.entry_date ?? undefined,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      qc.invalidateQueries({ queryKey: journalEntryKey(entry.id) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId, entry.patient_id) });
    },
  });
}

export function useArchiveJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('archive_journal_entry', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      qc.invalidateQueries({ queryKey: journalEntryKey(entry.id) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId, entry.patient_id) });
      qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    },
  });
}

export function useRestoreJournalEntry(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data, error } = await supabase.rpc('restore_journal_entry', { p_id: id });
      if (error) throw error;
      return data;
    },
    onSuccess: (entry) => {
      if (!entry || !clinicId) return;
      qc.invalidateQueries({ queryKey: journalEntryKey(entry.id) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId) });
      qc.invalidateQueries({ queryKey: journalEntriesKey(clinicId, entry.patient_id) });
    },
  });
}

// ---- Lock mutations ----

export function useAcquireJournalLock(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string): Promise<JournalLockState> => {
      const { data, error } = await supabase.rpc('acquire_journal_lock', {
        p_entry_id: entryId,
      });
      if (error) throw error;
      const row = Array.isArray(data) ? data[0] : data;
      return row as JournalLockState;
    },
    onSettled: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    },
  });
}

export function useReleaseJournalLock(clinicId: string | null | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entryId: string) => {
      const { error } = await supabase.rpc('release_journal_lock', { p_entry_id: entryId });
      if (error) throw error;
    },
    onSettled: () => {
      if (clinicId) qc.invalidateQueries({ queryKey: journalLocksKey(clinicId) });
    },
  });
}

export function useHeartbeatJournalLock() {
  return useMutation({
    mutationFn: async (entryId: string): Promise<string> => {
      const { data, error } = await supabase.rpc('heartbeat_journal_lock', {
        p_entry_id: entryId,
      });
      if (error) throw error;
      return data as string;
    },
  });
}

/**
 * Sends a heartbeat every 60 seconds while the editor is mounted.
 * Returns `'lock_lost'` signal via onLockLost callback.
 */
export function useJournalHeartbeat(
  entryId: string | null | undefined,
  active: boolean,
  onLockLost: () => void,
) {
  const heartbeat = useHeartbeatJournalLock();
  const onLockLostRef = useRef(onLockLost);
  onLockLostRef.current = onLockLost;

  useEffect(() => {
    if (!entryId || !active) return;

    const interval = setInterval(async () => {
      try {
        const result = await heartbeat.mutateAsync(entryId);
        if (result === 'lock_lost') {
          onLockLostRef.current();
        }
      } catch {
        onLockLostRef.current();
      }
    }, 60_000);

    return () => clearInterval(interval);
  }, [entryId, active]); // eslint-disable-line react-hooks/exhaustive-deps
}
